import React, { useMemo, useState } from 'react';
import { usePos } from '../context/PosContext';
import { formatDate, formatDateTime } from '../services/datetime';
import { printReportAuto } from '../services/receipt';
import { Transaction, TransportInfo } from '../types';
import './Sections.css';
import './Reports.css';
import './TransportReport.css';

// Transport Summary report (transport_report_layout.xml / transport_grouphead.xml)
// plus the Transport Detail entry form (transport_details.xml). Lists bills that
// carry transport / delivery info over a date range, and lets you attach a
// transport detail (vehicle, driver, in/out time, distance, destination…) to any
// recent bill. Android stores these in a Firestore TRANSPORT collection + on the
// bill; here they live on Transaction.transport (+ a saved-vehicle autocomplete).

type RangeKey = 'today' | 'yesterday' | 'month' | 'custom';

const EMPTY_DETAIL: TransportInfo = {
  vehicleNo: '',
  driverName: '',
  outTime: '',
  inTime: '',
  distance: '',
  deliveryStatus: '',
  destination: '',
  deliveryNote: '',
  other: '',
};

// Largest split = SPLIT, otherwise the single tender.
const paymentLabel = (s: Transaction): string => {
  if (s.payments && s.payments.length > 1) return 'SPLIT';
  return (s.paymentMethod || 'cash').toUpperCase();
};

// A bill is "transport-relevant" once it has any transport detail or a vehicle no.
const hasTransport = (s: Transaction): boolean =>
  !!s.transport?.vehicleNo || !!s.vehicleNo || !!s.deliveryChallanNo;

const TransportReport: React.FC = () => {
  const { sales, customers, salesmen, vehicles, saveVehicle, updateSale, formatMoney } = usePos();

  const today = new Date().toISOString().split('T')[0];
  const [range, setRange] = useState<RangeKey>('today');
  const [start, setStart] = useState(today);
  const [end, setEnd] = useState(today);

  // Transport Detail dialog state.
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [detail, setDetail] = useState<TransportInfo>(EMPTY_DETAIL);

  const period = useMemo(() => {
    const now = new Date();
    const iso = (d: Date) => d.toISOString().split('T')[0];
    if (range === 'today') return { from: today, to: today };
    if (range === 'yesterday') {
      const y = iso(new Date(Date.now() - 86400000));
      return { from: y, to: y };
    }
    if (range === 'month') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    return { from: start, to: end };
  }, [range, start, end, today]);

  const inRange = (iso: string) => {
    const t = new Date(iso).getTime();
    return t >= new Date(period.from).getTime() && t <= new Date(period.to + 'T23:59:59').getTime();
  };

  const completed = useMemo(
    () => sales.filter((s) => s.status === 'completed' && s.total > 0 && inRange(s.timestamp)),
    [sales, period] // eslint-disable-line
  );

  // Rows for the report: only transport-relevant bills, newest first.
  const rows = useMemo(
    () =>
      completed
        .filter(hasTransport)
        .sort((a, b) => +new Date(b.timestamp) - +new Date(a.timestamp)),
    [completed]
  );

  // Bills with no transport detail yet — offered in the "attach detail" picker.
  const pending = useMemo(
    () => completed.filter((s) => !hasTransport(s)).sort((a, b) => +new Date(b.timestamp) - +new Date(a.timestamp)),
    [completed]
  );

  const phoneOf = (s: Transaction) =>
    customers.find((c) => c.id === s.customerId)?.phone || '';

  const total = rows.reduce((a, s) => a + s.total, 0);

  const COLUMNS = [
    'NO', 'Vehicle No', 'Date Time', 'Customer Name', 'Mobile', 'Payment Mode',
    'Destination', 'Out Time', 'Distance', 'Other', 'Delivery Note', 'Amount',
  ];

  const cellsFor = (s: Transaction, i: number): (string | number)[] => {
    const t = s.transport;
    return [
      i + 1,
      t?.vehicleNo || s.vehicleNo || '—',
      formatDateTime(s.timestamp),
      s.customerName || 'Walk-in',
      phoneOf(s) || '—',
      paymentLabel(s),
      t?.destination || '—',
      t?.outTime || '—',
      t?.distance || '—',
      t?.other || '—',
      t?.deliveryNote || s.deliveryChallanNo || '—',
      formatMoney(s.total),
    ];
  };

  const openDetail = (s: Transaction) => {
    setEditing(s);
    setDetail({ ...EMPTY_DETAIL, ...(s.transport ?? {}), vehicleNo: s.transport?.vehicleNo || s.vehicleNo || '' });
  };

  const pickVehicle = (vno: string) => {
    const saved = vehicles.find((v) => v.vehicleNo.trim().toUpperCase() === vno.trim().toUpperCase());
    if (saved) setDetail((d) => ({ ...d, ...saved }));
    else setDetail((d) => ({ ...d, vehicleNo: vno }));
  };

  const submitDetail = () => {
    if (!editing) return;
    if (!detail.vehicleNo.trim()) {
      alert('Enter Vehicle No');
      return;
    }
    const clean: TransportInfo = { ...detail, vehicleNo: detail.vehicleNo.trim() };
    updateSale({ ...editing, transport: clean, vehicleNo: clean.vehicleNo });
    saveVehicle(clean);
    setEditing(null);
  };

  const exportCsv = () => {
    const data = [COLUMNS, ...rows.map((s, i) => cellsFor(s, i))];
    const csv = data.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Transport-Summary-${period.from}_${period.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printUsb = () => {
    if (!rows.length) return;
    const data: (string | number)[][] = rows.map((s, i) => cellsFor(s, i));
    data.push(['TOTAL', '', '', '', '', '', '', '', '', '', '', formatMoney(total)]);
    printReportAuto('Transport Summary', `${formatDate(period.from)} -> ${formatDate(period.to)}`, COLUMNS, data);
  };

  return (
    <div className="section-page tr-page">
      <div className="page-header">
        <h1>Transport Summary</h1>
        <div className="tr-head-actions">
          <select value={range} onChange={(e) => setRange(e.target.value as RangeKey)}>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="month">This Month</option>
            <option value="custom">Custom Date</option>
          </select>
        </div>
      </div>

      {range === 'custom' && (
        <div className="field-row">
          <div className="field">
            <label>Start</label>
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
          </div>
          <div className="field">
            <label>End</label>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
          </div>
        </div>
      )}

      <div className="section-card">
        <div className="rp-result-head">
          <p className="muted" style={{ margin: 0 }}>
            {formatDate(period.from)} → {formatDate(period.to)} · {rows.length} trip{rows.length === 1 ? '' : 's'}
          </p>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn-grey" onClick={() => window.print()} disabled={!rows.length}>Print</button>
            <button className="btn-grey" onClick={printUsb} disabled={!rows.length}>🖨 USB Print</button>
            <button className="btn-grey" onClick={exportCsv} disabled={!rows.length}>CSV</button>
          </div>
        </div>

        <div className="tr-scroll">
          <table className="section-table tr-table">
            <thead>
              <tr>
                {COLUMNS.map((c, i) => (
                  <th key={i} className={c === 'Amount' ? 'right' : ''}>{c}</th>
                ))}
                <th></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length + 1} className="muted">No transport records in range.</td>
                </tr>
              ) : (
                rows.map((s, i) => (
                  <tr key={s.id}>
                    {cellsFor(s, i).map((cell, ci) => (
                      <td key={ci} className={ci === COLUMNS.length - 1 ? 'right' : ''}>{cell}</td>
                    ))}
                    <td>
                      <button className="btn-link" onClick={() => openDetail(s)}>Edit</button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={COLUMNS.length - 1} className="right tr-total-lbl">TOTAL</td>
                  <td className="right tr-total-lbl">{formatMoney(total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Attach a transport detail to a bill that doesn't have one yet. */}
      <div className="section-card">
        <h2 style={{ marginTop: 0 }}>Add Transport Detail</h2>
        {pending.length === 0 ? (
          <p className="muted">Every bill in this range already has a transport detail.</p>
        ) : (
          <table className="section-table">
            <thead>
              <tr><th>Date Time</th><th>Bill No</th><th>Customer</th><th className="right">Amount</th><th></th></tr>
            </thead>
            <tbody>
              {pending.slice(0, 25).map((s) => (
                <tr key={s.id}>
                  <td>{formatDateTime(s.timestamp)}</td>
                  <td>{s.billNo}</td>
                  <td>{s.customerName || 'Walk-in'}</td>
                  <td className="right">{formatMoney(s.total)}</td>
                  <td><button className="btn-teal" onClick={() => openDetail(s)}>+ Detail</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <div className="modal-overlay" onClick={() => setEditing(null)}>
          <div className="section-card tr-detail" onClick={(e) => e.stopPropagation()}>
            <div className="rp-result-head">
              <h2 style={{ margin: 0 }}>Transport Detail · Bill {editing.billNo}</h2>
              <button className="btn-grey" onClick={() => setEditing(null)}>✕</button>
            </div>

            <div className="tr-form-grid">
              <label>
                Vehicle No*
                <input
                  list="tr-vehicle-list"
                  value={detail.vehicleNo}
                  onChange={(e) => pickVehicle(e.target.value)}
                  placeholder="Search Vehicle No"
                  style={{ textTransform: 'uppercase' }}
                />
                <datalist id="tr-vehicle-list">
                  {vehicles.map((v) => <option key={v.vehicleNo} value={v.vehicleNo} />)}
                </datalist>
              </label>

              <label>
                Driver Name
                <select value={detail.driverName || ''} onChange={(e) => setDetail((d) => ({ ...d, driverName: e.target.value }))}>
                  <option value="">— Select —</option>
                  {salesmen.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                </select>
              </label>

              <label>
                Out Time
                <input value={detail.outTime || ''} onChange={(e) => setDetail((d) => ({ ...d, outTime: e.target.value }))} placeholder="Out Time" />
              </label>
              <label>
                In Time
                <input value={detail.inTime || ''} onChange={(e) => setDetail((d) => ({ ...d, inTime: e.target.value }))} placeholder="In Time" />
              </label>

              <label>
                Distance (Km)
                <input value={detail.distance || ''} onChange={(e) => setDetail((d) => ({ ...d, distance: e.target.value }))} placeholder="Distance (Km)" />
              </label>
              <label>
                Delivery Status
                <div className="tr-radio">
                  <label className="tr-radio-opt">
                    <input type="radio" name="tr-delivery" checked={detail.deliveryStatus === 'Yes'} onChange={() => setDetail((d) => ({ ...d, deliveryStatus: 'Yes' }))} /> Yes
                  </label>
                  <label className="tr-radio-opt">
                    <input type="radio" name="tr-delivery" checked={detail.deliveryStatus === 'No'} onChange={() => setDetail((d) => ({ ...d, deliveryStatus: 'No' }))} /> No
                  </label>
                </div>
              </label>

              <label>
                Destination
                <input value={detail.destination || ''} onChange={(e) => setDetail((d) => ({ ...d, destination: e.target.value }))} placeholder="Destination" />
              </label>
              <label>
                Delivery Note
                <input value={detail.deliveryNote || ''} onChange={(e) => setDetail((d) => ({ ...d, deliveryNote: e.target.value }))} placeholder="Delivery Note" />
              </label>

              <label className="tr-span2">
                Other
                <input value={detail.other || ''} onChange={(e) => setDetail((d) => ({ ...d, other: e.target.value }))} placeholder="Other" />
              </label>
            </div>

            <div className="tr-form-actions">
              <button className="btn-red" onClick={() => setEditing(null)}>Cancel</button>
              <button className="btn-teal" onClick={submitDetail}>Submit</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportReport;
